import { PropsWithChildren, ReactNode, useCallback } from 'react';

import { Box, Drawer, styled } from '@mui/material';

const DEFAULT_WIDTH = 400;

const DrawerContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.drawer,
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3),
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2, 3),
}));

const DrawerBody = styled(Box)({
  flex: 1,
  overflow: 'auto',

  '&.no-scroll': {
    overflow: 'hidden',
  },
});

const DrawerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3, 3),
  minHeight: '100%',

  '&.no-padding': {
    padding: 0,
  },
}));

export interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  onBodyHeightChange?: (height: number) => void;
  noScroll?: boolean;
  width?: number;
  variant?: 'permanent' | 'persistent' | 'temporary';
  noBackdrop?: boolean;
}

function useAppDrawer({ onBodyHeightChange }: Pick<AppDrawerProps, 'onBodyHeightChange'>) {
  const bodyRef = useCallback(
    (node: HTMLDivElement) => {
      if (!node) return;
      const resizeObserver = new ResizeObserver(() => {
        if (onBodyHeightChange) {
          onBodyHeightChange(node.offsetHeight);
        }
      });
      resizeObserver.observe(node);
    },
    [onBodyHeightChange],
  );

  return { bodyRef };
}

export function AppDrawer({
  open,
  onClose,
  header,
  footer,
  children,
  onBodyHeightChange,
  width,
  noScroll,
  variant,
  noBackdrop,
}: PropsWithChildren<AppDrawerProps>) {
  const { bodyRef } = useAppDrawer({ onBodyHeightChange });

  return (
    <Drawer
      anchor="right"
      open={open}
      variant={variant}
      onClose={onClose}
      ModalProps={
        noBackdrop
          ? {
              slotProps: {
                backdrop: {
                  invisible: true,
                },
              },
            }
          : undefined
      }
    >
      <DrawerContainer sx={{ width: width || DEFAULT_WIDTH }}>
        {header && <DrawerHeader>{header}</DrawerHeader>}
        <DrawerBody ref={bodyRef} className={noScroll ? 'no-scroll' : ''}>
          <DrawerContent className={noScroll ? 'no-padding' : ''}>{children}</DrawerContent>
        </DrawerBody>
        {footer && <DrawerFooter>{footer}</DrawerFooter>}
      </DrawerContainer>
    </Drawer>
  );
}
